import { Box, Divider, LinearProgress, Link, Stack, Typography } from "@mui/material";
import { type FC, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { NoAppsBanner } from "../../modules/channels/slack/components/no-apps-banner";
import { SlackAlias } from "../../modules/channels/slack/components/slack-alias/alias";
import { SlackCardV2 } from "../../modules/channels/slack/components/slack-card-v2";
import type { SlackAppConfig } from "../../modules/channels/slack/types";
import { RouteTabs } from "../../modules/generic/components/route-tabs";
import { Page } from "../../modules/layout/components/page";
import { PageHeader } from "../../modules/layout/components/page-header";
import { PageHeading } from "../../modules/layout/components/page-heading";
import { useFlag } from "../../server/optimizely";
import { useSupabaseLoader } from "../../server/supabase/hooks";
import { SMS } from "./sms";
import { Teams } from "./teams";
import { Email } from "./email";
import { WhatsApp } from "./whatsapp";

const DefaultPage: FC<{ channel?: "slack" | "email" }> = ({ channel }) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (channel) void navigate(`/manage/channels/${channel}`, { replace: true });
  });
  return null;
};

const SlackV2: FC = () => {
  const {
    data: slackApps,
    loading: slackAppsLoading,
    error: slackAppsError,
    reload: reloadSlackApps,
  } = useSupabaseLoader<SlackAppConfig[]>(async ({ supabase }) => {
    const { data, error } = await supabase
      .from("slack_app_config")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) throw new Error("Could not load slack app configs");
    return data ?? [];
  }, []);

  if (slackAppsLoading || slackAppsError || !slackApps) return null;

  return slackApps.length > 0 ? (
    <Stack spacing={4} ml={2}>
      <Typography variant="body2" fontWeight={400} fontSize="14px">
        Integrate your Communications with Slack. Send direct messages to your employees with
        ChangeEngine.
      </Typography>
      <Box>
        <Typography
          variant="overline"
          fontWeight={600}
          fontSize="12px"
          lineHeight="250%"
          letterSpacing="0.5px"
          color="text.secondary"
        >
          WORKSPACE CONNECTORS
        </Typography>
        <Typography variant="body2" fontWeight={400} fontSize="14px">
          Running more workspaces? Let’s power them all. Reach out to our Implementation team at{" "}
          {<Link href="mailto:onboarding@changeengine.com">onboarding@changeengine.com</Link>} to
          connect your additional workspace today.
        </Typography>
      </Box>
      <Stack direction="row" spacing={2}>
        {[...slackApps]
          .sort((a, b) => (a.slack_config_id ? 0 : 1) - (b.slack_config_id ? 0 : 1))
          .map((slackApp) => (
            <SlackCardV2 key={slackApp.id} slackApp={slackApp} reload={reloadSlackApps} />
          ))}
      </Stack>
      {slackApps.length > 0 && (
        <>
          <Divider sx={{ mt: 3, mb: 3 }} />
          <SlackAlias />
        </>
      )}
    </Stack>
  ) : (
    <NoAppsBanner />
  );
};

const ManageChannelsPage: FC = () => {
  const [whatsAppEnabled, , whatsAppFlagLoaded] = useFlag("whatsapp-communications");
  const { pathname } = useLocation();
  const isWhatsAppRoute =
    pathname === "/manage/channels/whatsapp" || pathname.startsWith("/manage/channels/whatsapp/");

  if (isWhatsAppRoute && !whatsAppFlagLoaded) return <LinearProgress />;

  if (isWhatsAppRoute && !whatsAppEnabled) {
    return <Navigate replace to="/manage/channels/slack" />;
  }

  return (
    <Page title="Channels | ChangeEngine">
      <PageHeader>
        <PageHeading heading="Channels" subheading="Manage your message delivery channels" />
      </PageHeader>
      <RouteTabs
        base="/manage/channels/"
        defaultPage={<DefaultPage channel="slack" />}
        tabs={[
          {
            type: "inferred",
            data: { name: "Slack" },
            component: <SlackV2 />,
          },
          { type: "inferred", data: { name: "Email" }, component: <Email /> },
          { type: "inferred", data: { name: "Teams" }, component: <Teams /> },
          { type: "inferred", data: { name: "SMS" }, component: <SMS /> },
          ...(whatsAppEnabled
            ? [{ type: "inferred" as const, data: { name: "WhatsApp" }, component: <WhatsApp /> }]
            : []),
        ]}
      />
    </Page>
  );
};

export default ManageChannelsPage;
